import { useSelector } from 'react-redux'
import { RootState } from '@/redux/reducers'
import {
  LimitTemplateBasePrice,
  LimitTemplateSpecialPrice,
  ResponseProcessLimit,
} from '@/redux/saga/limitHandlerSaga'
import {
  LimitStatus,
  LimitTypeResult,
  GetLimitTypeUIHandler,
} from '../../../ui-components/src/components/BetLotto/PanelSelection/PanelSelection.types'

// Selector for getting limit template from Redux store
const selectLimitTemplate = (state: RootState) => state.limitTemplate.limitRound

/**
 * Helper function to check if a value is within a range
 */
export const isInRange = (
  currentPrice: number,
  minPrice?: number | null,
  maxPrice?: number | null,
): boolean => {
  if (minPrice !== undefined && minPrice !== null && currentPrice < minPrice) {
    return false
  }
  if (maxPrice !== undefined && maxPrice !== null && currentPrice > maxPrice) {
    return false
  }
  return true
}

/**
 * Check if a bet exceeds a limit rule
 */
const checkLimitRule = (
  currentBet: number,
  rule: LimitTemplateBasePrice | LimitTemplateSpecialPrice,
): boolean => {
  return (
    rule.type === 'limit' &&
    rule.limit_price != null &&
    currentBet >= rule.limit_price
  )
}

/**
 * Check if a bet is within a range rule
 */
const checkRangeRule = (
  currentBet: number,
  rule: LimitTemplateBasePrice | LimitTemplateSpecialPrice,
): boolean => {
  if (rule.type !== 'range') return false

  const min = rule.bet_price_min ?? -Infinity
  const max = rule.bet_price_max ?? Infinity
  return isInRange(currentBet, min, max) || currentBet > max
}

/**
 * Check rules for a specific bet type and number
 */
const checkRulesForBetType = (
  betType: string,
  number: string,
  currentBet: number,
  rules: (LimitTemplateBasePrice | LimitTemplateSpecialPrice)[],
): LimitStatus => {
  // Filter rules that apply to this bet type and number
  const applicableRules = rules.filter(
    (rule) =>
      rule.bet_type === betType &&
      ('bet_number' in rule ? rule.bet_number === number : true),
  )

  // Check each applicable rule
  for (const rule of applicableRules) {
    if (checkLimitRule(currentBet, rule)) {
      return 'limit'
    }
    if (checkRangeRule(currentBet, rule)) {
      return 'range'
    }
  }

  return 'none'
}

/**
 * Check if a number has special rules
 */
const hasSpecialRules = (
  number: string,
  specialRules: LimitTemplateSpecialPrice[],
): boolean => {
  return specialRules.some((rule) => rule.bet_number === number)
}

/**
 * Get current bet amount for a number and bet type
 */
const getCurrentBet = (
  currentPrice: ResponseProcessLimit['current_price'],
  betType: string,
  number: string,
): number => {
  return currentPrice.current_bet[betType]?.[number]?.current_bet || 0
}

/**
 * Determines the limit type for a given number and bet types
 */
export const getLimitTypeUI = (
  number: string,
  betTypes: string[] = [],
  limitTemplate?: ResponseProcessLimit,
): LimitTypeResult => {
  // Early return if no limit template exists
  if (!limitTemplate) {
    return { limit: 'none' }
  }

  const {
    current_price,
    base_limit_price = [],
    special_limit_price = [],
  } = limitTemplate

  // Check each bet type
  for (const betType of betTypes) {
    const currentBet = getCurrentBet(current_price, betType, number)

    // Check if this number has special rules
    if (hasSpecialRules(number, special_limit_price)) {
      const result = checkRulesForBetType(
        betType,
        number,
        currentBet,
        special_limit_price,
      )
      if (result !== 'none') {
        return { limit: result }
      }
    } else {
      // Use base rules if no special rules exist
      const result = checkRulesForBetType(
        betType,
        number,
        currentBet,
        base_limit_price,
      )
      if (result !== 'none') {
        return { limit: result }
      }
    }
  }

  return { limit: 'none' }
}

/**
 * Hook that provides getLimitTypeUI function with automatic Redux integration
 */
export const useGetLimitTypeUI = (): GetLimitTypeUIHandler => {
  const limitTemplate = useSelector(selectLimitTemplate)

  return (number: string, betTypes: string[] = []): LimitTypeResult => {
    return getLimitTypeUI(number, betTypes, limitTemplate)
  }
}
